/**
 * QIPSharePage — the public read-only scorecard URL handed to the QIP
 * assessor. NO Header (no nav / no login link), NO bubble, NO bottom-nav.
 * Just the practice identity, readiness scorecard, and governance summary.
 *
 * URL: /qip-share/{token}
 *
 * The token validates server-side; if revoked / expired the API returns
 * 410 and we render a clean "this link is no longer active" page.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Shield, CheckCircle2, AlertTriangle, Lock, Sparkles, FileBadge, IdCard, Building2, Radiation } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function color(score) {
  if (score >= 90) return '#16A34A';
  if (score >= 70) return '#0EA5E9';
  if (score >= 50) return '#D97706';
  return '#DC2626';
}

export default function QIPSharePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/qip-share/public/${token}`)
      .then(r => setData(r.data))
      .catch(e => {
        const code = e.response?.status;
        const msg = e.response?.data?.detail || 'Could not load this share link.';
        setErr({ code, msg });
      })
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center text-slate-500">
        Loading scorecard…
      </div>
    );
  }
  if (err) {
    return <ErrorScreen code={err.code} msg={err.msg} />;
  }

  const p = data.practice || {};
  const sc = data.scorecard || {};
  const overall = sc.overall_score || 0;
  const overallColor = color(overall);
  const addrLine = [p.address_line_1, p.address_line_2, p.suburb,
                    [p.state, p.postcode].filter(Boolean).join(' '),
                    p.country].filter(Boolean).join(', ');

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100" data-testid="qip-share-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Audit banner */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider"
            style={{ backgroundColor: `${overallColor}1A`, color: overallColor }}>
            <Shield className="w-3 h-3" /> DIAS audit pre-visit · read-only
          </div>
        </div>

        {/* Practice identity */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-4 shadow-sm">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-4 h-4" style={{ color: '#42CDD7' }} />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#42CDD7' }}>Practice</span>
              </div>
              <h1 className="font-bold text-3xl sm:text-4xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {p.practice_name || 'Practice'}
              </h1>
              {addrLine && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{addrLine}</p>}
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-mono mt-2 text-slate-700 dark:text-slate-300">
                {p.abn && <span><strong>ABN</strong> {p.abn}</span>}
                {p.lspn && <span><strong>LSPN</strong> {p.lspn}</span>}
                {p.hpi_o && <span><strong>HPI-O</strong> {p.hpi_o}</span>}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] mt-1 text-slate-500">
                {p.phone && <span>Ph {p.phone}</span>}
                {p.fax && <span>Fax {p.fax}</span>}
                {p.email && <span>{p.email}</span>}
              </div>
            </div>
            <ScoreRing value={overall} color={overallColor} />
          </div>
        </div>

        {/* Scorecard buckets */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 mb-4 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>DIAS readiness scorecard</h2>
            <span className="text-[10px] text-slate-500">As of {sc.as_of}</span>
          </div>
          <div className="space-y-3">
            {(sc.buckets || []).map(b => (
              <BucketRow key={b.label} bucket={b} />
            ))}
          </div>
        </div>

        {/* Radiation Governance summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <SummaryCard icon={IdCard} label="Radiation Safety Officer" value={data.rso?.name || '—'}
            sub={data.rso?.appointed_at ? `Appointed ${data.rso.appointed_at}` : 'Not yet appointed'} />
          <SummaryCard icon={FileBadge} label="EPA Radiation Licence" value={data.epa_licence?.licence_number || '—'}
            sub={data.epa_licence?.expires_at ? `Expires ${data.epa_licence.expires_at}` : 'Not yet recorded'} />
          <SummaryCard icon={Radiation} label="RSO-required equipment" value={`${data.equipment_summary?.rso_required_count || 0} asset(s)`}
            sub={`${data.equipment_summary?.active_count || 0} active assets total`} />
          <SummaryCard icon={CheckCircle2} label="DIAS sub-checks passing"
            value={`${(sc.buckets || []).reduce((a, b) => a + b.passed, 0)} / ${(sc.buckets || []).reduce((a, b) => a + b.total, 0)}`}
            sub="Across the three entry-level standards" />
        </div>

        {/* Footer */}
        <div className="text-center text-[11px] text-slate-500 mt-6 pb-4 flex items-center justify-center gap-2 flex-wrap">
          <Lock className="w-3 h-3" />
          <span>Read-only audit share. Token expires {new Date(data.expires_at).toLocaleDateString('en-AU')}.</span>
          <span className="px-1">·</span>
          <Sparkles className="w-3 h-3" />
          <span>Generated by i3 Radiology Compliance Assistant</span>
        </div>
      </div>
    </div>
  );
}

function BucketRow({ bucket }) {
  const c = color(bucket.score);
  return (
    <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-3" data-testid={`qip-bucket-${bucket.label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold">{bucket.label}</p>
        <span className="text-xs font-bold tabular-nums" style={{ color: c }}>
          {bucket.score}% · {bucket.passed}/{bucket.total}
        </span>
      </div>
      <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden mb-3">
        <div className="h-full rounded-full transition-all" style={{ width: `${bucket.score}%`, backgroundColor: c }} />
      </div>
      <ul className="space-y-1.5 text-xs">
        {bucket.items.map((it, idx) => (
          <li key={idx} className="flex items-center gap-2">
            {it.ok
              ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
              : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
            <span className="flex-1">{it.label}</span>
            <span className="text-[10px] text-slate-500 tabular-nums">{it.detail}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-3.5 h-3.5 text-slate-500" />
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</p>
      </div>
      <p className="text-base font-semibold truncate">{value}</p>
      {sub && <p className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</p>}
    </div>
  );
}

function ScoreRing({ value, color }) {
  const size = 96, stroke = 8, r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (value / 100) * circ;
  return (
    <svg width={size} height={size} className="-rotate-90 shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth={stroke}
        fill="transparent" className="text-slate-200 dark:text-slate-800" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke}
        fill="transparent" strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" />
      <text x="50%" y="50%" dy="0.36em" textAnchor="middle"
        className="text-2xl font-bold fill-current rotate-90 origin-center"
        transform={`rotate(90 ${size / 2} ${size / 2})`}>{value}</text>
    </svg>
  );
}

function ErrorScreen({ code, msg }) {
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <Lock className="w-12 h-12 mx-auto text-slate-400 mb-3" />
        <h1 className="font-bold text-2xl mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Share link unavailable</h1>
        <p className="text-sm text-slate-500">{msg}</p>
        {code && <p className="text-[10px] text-slate-400 mt-2">Error {code}</p>}
        <p className="text-xs text-slate-500 mt-6">Please contact i3 Radiology to request a new audit link.</p>
      </div>
    </div>
  );
}
